/**
 * Export Utilities
 * Provides functions for exporting data to various formats
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-4
 *
 * @description
 * Utility functions for data export:
 * - CSV export with proper escaping
 * - Automatic download trigger
 * - Column configuration support
 */

export interface ExportColumn {
  /**
   * Key in the data object
   */
  key: string;

  /**
   * Display label for the column
   */
  label: string;
}

/**
 * Escape CSV value to handle quotes and commas
 */
function escapeCsvValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // If value contains comma, quote, or newline, wrap it in quotes and escape existing quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Export data to CSV format and trigger download
 *
 * @param data - Array of data objects to export
 * @param filename - Filename for the download (without extension)
 * @param columns - Column configuration with keys and labels
 *
 * @example
 * ```typescript
 * exportToCsv(
 *   transactions,
 *   'transactions-export',
 *   [
 *     { key: 'batchCode', label: 'Batch Code' },
 *     { key: 'gaushalaName', label: 'Gaushala' },
 *     { key: 'quantityKg', label: 'Quantity (KG)' }
 *   ]
 * );
 * ```
 */
export function exportToCsv<T extends Record<string, any>>(
  data: T[],
  filename: string,
  columns: ExportColumn[]
): void {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create CSV header
  const header = columns.map((col) => escapeCsvValue(col.label)).join(',');

  // Create CSV rows
  const rows = data.map((item) => {
    return columns
      .map((col) => {
        const value = item[col.key];
        return escapeCsvValue(value);
      })
      .join(',');
  });

  // Combine header and rows
  const csv = [header, ...rows].join('\n');

  // Create blob and trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

/**
 * Format date for CSV export
 */
export function formatDateForExport(dateString: string | undefined | null): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toISOString();
  } catch {
    return dateString || '';
  }
}

/**
 * Format number for CSV export
 */
export function formatNumberForExport(value: number | undefined | null): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * Format currency for CSV export (without currency symbol)
 */
export function formatCurrencyForExport(amount: number | undefined | null): string {
  if (amount === null || amount === undefined) return '';
  return amount.toFixed(2);
}

export default {
  exportToCsv,
  formatDateForExport,
  formatNumberForExport,
  formatCurrencyForExport,
};
